import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Progress } from 'antd';
import { DatabaseOutlined, InfoCircleOutlined, MessageOutlined, PhoneOutlined } from '@ant-design/icons';
import { CardLayout, cardLayoutOptions } from '../../Layout';
import { IPackageListPaginated, ITariffPackageInstance } from '../../../interfaces';
import { getTariffPackageInstances } from '../../../middleware';
import { UnitTypeValues } from '../../../middleware';
import { showOriginalUnitByType } from '../../../instruments';
import { InfiniteScroll } from '../../Tariffs/Components/InfiniteScroll';
import Styles from './m_packageUsage.less';

interface IPackageUsageStatisticsProps {
    item: ITariffPackageInstance;
}

interface IPackageUsageProps {
    cardId: string;
}

const PackageUsageStatistics: React.FC<IPackageUsageStatisticsProps> = ({ item }: IPackageUsageStatisticsProps) => {
    const { tariffPackage, dataVolumePercentageUsage, smsVolumePercentageUsage, voiceVolumePercentageUsage } = item;
    const { name, dataVolume = 0, smsVolume = 0, voiceVolume = 0 } = tariffPackage;

    const dataVolumeUsageNumber = dataVolume - dataVolume * (dataVolumePercentageUsage / 100);
    const voiceVolumeUsageNumber = voiceVolume - voiceVolume * (voiceVolumePercentageUsage / 100);
    const smsVolumeUsageNumber = smsVolume - smsVolume * (smsVolumePercentageUsage / 100);

    return (
        <div className={Styles.packageCard}>
            <h3 className={Styles.packageTitle}>{name}</h3>

            <div className={Styles.usageSection}>
                <div className={Styles.usageHeader}>
                    <div className={Styles.usageLabel}>
                        <DatabaseOutlined /> Data Usage
                    </div>
                    <div className={Styles.usagePercentage}>{(100 - dataVolumePercentageUsage).toFixed(2)}%</div>
                </div>
                <div className={Styles.usageStats}>
                    <div>{showOriginalUnitByType(`${dataVolumeUsageNumber}`, UnitTypeValues.DATA)} used</div>
                    <div>{showOriginalUnitByType(`${dataVolume}`, UnitTypeValues.DATA)} total</div>
                </div>
                <Progress percent={100 - dataVolumePercentageUsage} showInfo={false} strokeColor={'#1890ff'} />
            </div>

            <div className={Styles.usageSection}>
                <div className={Styles.usageHeader}>
                    <div className={Styles.usageLabel}>
                        <PhoneOutlined /> Call Minutes
                    </div>
                    <div className={Styles.usagePercentage}>{(100 - voiceVolumePercentageUsage).toFixed(2)}%</div>
                </div>
                <div className={Styles.usageStats}>
                    <div>{showOriginalUnitByType(`${voiceVolumeUsageNumber}`, UnitTypeValues.VOICE)} used</div>
                    <div>{showOriginalUnitByType(`${voiceVolume}`, UnitTypeValues.VOICE)} total</div>
                </div>
                <Progress percent={100 - voiceVolumePercentageUsage} showInfo={false} strokeColor={'#1890ff'} />
            </div>

            <div className={Styles.usageSection}>
                <div className={Styles.usageHeader}>
                    <div className={Styles.usageLabel}>
                        <MessageOutlined /> SMS Messages
                    </div>
                    <div className={Styles.usagePercentage}>{(100 - smsVolumePercentageUsage).toFixed(2)}%</div>
                </div>
                <div className={Styles.usageStats}>
                    <div>{showOriginalUnitByType(`${smsVolumeUsageNumber}`, UnitTypeValues.SMS)} used</div>
                    <div>{showOriginalUnitByType(`${smsVolume}`, UnitTypeValues.SMS)} total</div>
                </div>
                <Progress percent={100 - smsVolumePercentageUsage} showInfo={false} strokeColor={'#1890ff'} />
            </div>
        </div>
    );
};

export const PackageUsage = observer(({ cardId }: IPackageUsageProps) => {
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<ITariffPackageInstance[]>([]);
    const [total, setTotal] = useState(0);

    const pageSize = 25;

    const fetchData = async (pageNumber: number) => {
        const resp = (await getTariffPackageInstances(
            { cardId: cardId, pageNumber, pageSize, boughtForMyCompany: true },
            true,
        )) as IPackageListPaginated;

        if (resp) {
            const newItems = pageNumber === 1 ? resp.data : [...items, ...resp.data];

            setItems(newItems as ITariffPackageInstance[]);
            setTotal(resp.meta.records_count || items.length);
        }
    };

    useEffect(() => {
        setPage(1);
        setItems([]);
        fetchData(1);
    }, [cardId]);

    return (
        <CardLayout columnOption={cardLayoutOptions} title={'Packages'}>
            <div className={Styles.packageUsageContainer}>
                {items.length > 0 ? (
                    <InfiniteScroll
                        pageSize={pageSize}
                        page={page}
                        fetchFunc={fetchData}
                        totalPackage={total}
                        currentPackages={items.length}
                        handleSetPage={setPage}
                    >
                        {items.map((item: ITariffPackageInstance) => (
                            <div key={item.id}>
                                <PackageUsageStatistics item={item} />
                            </div>
                        ))}
                    </InfiniteScroll>
                ) : (
                    <div className={Styles.emptyState}>
                        <InfoCircleOutlined /> No packages found
                    </div>
                )}
            </div>
        </CardLayout>
    );
});
